import { z } from 'zod';
import {
  baseQueryArgs,
  baseExtraOptions,
  rsosApi,
} from '@rsos/reduxDataStore/src/features/rsosApi';

/**
 * The most interagency conversations a user can have muted. Muting beyond it
 * evicts the oldest mutes client-side; the backend schema's matching maxItems
 * is only a backstop against buggy writers.
 */
export const MAX_MUTED_CONVERSATIONS = 100;

const CAPSTONE_APPLICATION_NAME = 'capstone';

// Plain z.object() accepts and strips unknown keys, so backend additions to
// either object never fail older clients. Do not add .strict(). Stripping is
// also what retires the superseded subscribed_conversation_ids key: a user who
// still has one stored parses to no mutes, which is notified-everywhere.
const InteragencyChatUserSettingsSchema = z.object({
  muted_conversation_ids: z.array(z.string()).optional(),
});

const CapstoneUserAttributesSchema = z.object({
  interagency_chat: InteragencyChatUserSettingsSchema.nullable().optional(),
});

function toMutedConversationIds(response: unknown): string[] {
  const parsed = CapstoneUserAttributesSchema.parse(response);
  return parsed.interagency_chat?.muted_conversation_ids ?? [];
}

const InteragencyMutesTag = 'InteragencyMutes';

const enhancedApi = rsosApi.enhanceEndpoints({
  addTagTypes: [InteragencyMutesTag],
});

/**
 * Per-user interagency chat settings, stored in scorpius user attributes
 * (application `capstone`, key `interagency_chat`). User-scoped: scorpius
 * derives the user from the access token, so queries take no arguments.
 *
 * Writes go through PATCH /v2/scorpius/user/attributes only. The v2 endpoint
 * follows RFC 7386 (arrays replace wholesale), which is what makes the mute
 * list writable at all — the v1 PATCH appends to arrays and does not reliably
 * persist. Never write this key through the v1 PATCH.
 */
export const interagencyUserSettingsAPI = enhancedApi.injectEndpoints({
  endpoints: builder => ({
    getMutedInteragencyConversations: builder.query<string[], undefined | void>(
      {
        query: () => ({
          ...baseQueryArgs,
          // A user who has never stored any capstone setting 404s; that is an
          // empty mute list, not an error.
          validateStatus: (response: Response) =>
            (response.status >= 200 && response.status < 300) ||
            response.status === 404,
          url: `v1/scorpius/user/attributes`,
          method: 'GET',
          params: { application_name: CAPSTONE_APPLICATION_NAME },
        }),
        transformResponse: (
          response: unknown,
          meta?: { response?: Response },
        ) => {
          if (meta?.response?.status === 404) return [];
          return toMutedConversationIds(response);
        },
        providesTags: [InteragencyMutesTag],
        extraOptions: {
          ...baseExtraOptions,
        },
      },
    ),
    updateMutedInteragencyConversations: builder.mutation<
      string[],
      { mutedConversationIds: string[] }
    >({
      query: ({ mutedConversationIds }) => ({
        ...baseQueryArgs,
        url: `v2/scorpius/user/attributes`,
        method: 'PATCH',
        body: {
          application_name: CAPSTONE_APPLICATION_NAME,
          data: {
            interagency_chat: {
              muted_conversation_ids: mutedConversationIds,
            },
          },
        },
      }),
      transformResponse: (response: unknown) =>
        toMutedConversationIds(response),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        // Write the server's echoed list into the read cache instead of
        // refetching; no optimistic update — the bell renders stored state.
        const { data } = await queryFulfilled;
        dispatch(
          interagencyUserSettingsAPI.util.updateQueryData(
            'getMutedInteragencyConversations',
            undefined,
            () => data,
          ),
        );
      },
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useGetMutedInteragencyConversationsQuery,
  useUpdateMutedInteragencyConversationsMutation,
} = interagencyUserSettingsAPI;

type MutesSelector = ReturnType<
  typeof interagencyUserSettingsAPI.endpoints.getMutedInteragencyConversations.select
>;

/** The store-state shape the mute selector accepts. */
export type InteragencyMutesApiState = Parameters<MutesSelector>[0];

/**
 * Reads the cached mute list from the store. For callbacks that need the list
 * as of call time, not render time — a render-time snapshot can be stale by the
 * time a queued write computes its payload.
 */
export function selectMutedInteragencyConversationIds(
  state: Parameters<MutesSelector>[0],
): string[] {
  const result =
    interagencyUserSettingsAPI.endpoints.getMutedInteragencyConversations.select(
      undefined,
    )(state);
  return result.data ?? [];
}
